import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon } from '@persona-id/design-tokens/icons';
import { useCallback, useEffect, useState } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import Spacer from 'design-system/components/Spacer';

const SideBySideContainer = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: 300px;
  border-radius: ${BORDER_RADIUSES.base}px;
  margin-bottom: ${Spacing[5]};
`;

const GoogleMapsView = styled('div')`
  height: 100%;
  flex-grow: 1;
  border-radius: ${BORDER_RADIUSES.rounded};
`;

const ViewUnavailable = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  color: ${Color.CoreSlate800};
`;

const StyledIconAlertCircle = styled(CircleAlertIcon)`
  margin-bottom: ${Spacing[2]};
  path,
  rect {
    stroke: ${Color.CoreSlate800};
  }
`;

interface MapAndStreetViewProps {
  lat: number;
  lng: number;
  addressStreet1: string;
  addressStreet2: string;
  addressCity: string;
  addressSubdivision: string;
  addressPostalCode: string;
}

const MapAndStreetView = ({
  lat,
  lng,
  addressStreet1,
  addressStreet2,
  addressCity,
  addressSubdivision,
  addressPostalCode,
}: MapAndStreetViewProps) => {
  const address = [
    [addressStreet1, addressStreet2].filter(Boolean).join(' '),
    [addressCity, addressSubdivision, addressPostalCode].filter(Boolean).join(', '),
  ].join(' ');

  const [addressGeocoded, setAddressGeocoded] = useState(false);
  const [position, setPosition] = useState(new google.maps.LatLng(lat, lng));
  const [streetPosition, setStreetPosition] = useState<google.maps.LatLng | null>(null);
  const [mapAvailable, setMapAvailable] = useState(false);
  const [panoAvailable, setPanoAvailable] = useState(false);

  useEffect(() => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: address }, (results, status) => {
      let positionResult = null;
      if (status === 'OK' && results != null) {
        // This geocode gives a more precise lat, lng
        positionResult = results[0].geometry.location;
        setPosition(positionResult);
        setMapAvailable(true);
      }

      setAddressGeocoded(true);

      new google.maps.StreetViewService().getPanorama(
        {
          // Use less precise position as fallback if we weren't able to get one
          // via geocoder.geocode.
          location: positionResult || position,
          radius: 50,
          preference: google.maps.StreetViewPreference.NEAREST,
          source: google.maps.StreetViewSource.OUTDOOR,
        },
        (data: any) => {
          if (data && data.location && data.location.latLng) {
            setStreetPosition(data.location.latLng);
            setPanoAvailable(true);
          } else {
            setStreetPosition(null);
            setPanoAvailable(false);
          }
        },
      );
    });
  }, [address, position.lat(), position.lng()]);

  const callbackRefMap = useCallback(
    (mapRef: HTMLDivElement) => {
      if (mapRef == null) return;

      const map = new google.maps.Map(mapRef, {
        center: position,
        zoom: 14,
        disableDefaultUI: true,
      });

      new google.maps.Marker({
        map,
        position,
      });
    },
    [position.lat(), position.lng()],
  );

  const callbackRefPano = useCallback(
    (panoRef: HTMLDivElement) => {
      if (panoRef == null) return;
      if (streetPosition == null) return;

      new google.maps.StreetViewPanorama(panoRef, {
        position: streetPosition,
        pov: {
          heading: google.maps.geometry.spherical.computeHeading(streetPosition, position),
          pitch: 0,
        },
      });
    },
    [streetPosition?.lat(), streetPosition?.lng(), position.lat(), position.lng()],
  );

  return (
    <SideBySideContainer>
      {addressGeocoded && (
        <>
          {mapAvailable ? (
            <GoogleMapsView id='map' ref={callbackRefMap} data-chromatic='ignore' />
          ) : (
            <ViewUnavailable>
              <StyledIconAlertCircle />
              <span>Map is unavailable for this address.</span>
            </ViewUnavailable>
          )}
          <Spacer width={3} />
          {panoAvailable ? (
            <GoogleMapsView id='pano' ref={callbackRefPano} data-chromatic='ignore' />
          ) : (
            <ViewUnavailable>
              <StyledIconAlertCircle />
              <span>Street View data is unavailable for this address.</span>
            </ViewUnavailable>
          )}
        </>
      )}
    </SideBySideContainer>
  );
};

export default MapAndStreetView;
